import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import dayjs from 'dayjs';
import { useSpeechDictation } from '../../../../hooks/useSpeechDictation';
import api from '../../../../utils/functions/api';

const API = `${process.env.REACT_APP_API_URL}/ai-followup`;

const providerLabel = (key) => (key ? key.charAt(0) + key.slice(1).toLowerCase() : '');

// สถานะ: ตอบแล้ว ไม่เท่ากับปิดเรื่อง — การปิดต้องผ่านผู้มีอำนาจเสมอ
const STATUS_META = {
  pending_review: { label: 'รอหัวหน้าตรวจคำถาม', bg: '#fef3c7', color: '#92400e' },
  open: { label: 'รอตอบ', bg: '#fee2e2', color: '#991b1b' },
  answered: { label: 'ตอบแล้ว (รอตรวจสอบ)', bg: '#dbeafe', color: '#1e40af' },
  waiting_evidence: { label: 'รอหลักฐาน', bg: '#ffedd5', color: '#9a3412' },
  evidence_verified: { label: 'มีหลักฐานยืนยันแล้ว', bg: '#ccfbf1', color: '#115e59' },
  resolved: { label: 'แก้ไขแล้ว (รออนุมัติปิด)', bg: '#dcfce7', color: '#166534' },
  closed: { label: 'ปิดประเด็นแล้ว', bg: '#e5e7eb', color: '#374151' },
  dismissed: { label: 'ไม่อนุมัติคำถาม', bg: '#e5e7eb', color: '#374151' },
};
const PRIORITY_META = {
  high: { label: 'สำคัญสูง', bg: '#dc2626', color: '#fff' },
  medium: { label: 'สำคัญกลาง', bg: '#f59e0b', color: '#fff' },
  low: { label: 'สำคัญต่ำ', bg: '#9ca3af', color: '#fff' },
};
const ANSWERABLE = ['open', 'answered', 'waiting_evidence'];
const UNRESOLVED = ['open', 'answered', 'waiting_evidence', 'evidence_verified'];
const MAROON = '#8a1c1c';
const fmtDate = (d) => (d ? dayjs(d).format('D-M-YY') : '-');

// ปุ่มไมค์ท้ายช่องพิมพ์ (ใช้ซ้ำทั้งช่องตอบและหน้าต่างแก้ไขคำตอบ)
function MicAdornment({ supported, listening, start, stop }) {
  return (
    <InputAdornment position="end">
      <Tooltip
        title={
          !supported
            ? 'เบราว์เซอร์นี้ไม่รองรับ ใช้ Google Chrome หรือ Edge'
            : listening ? 'หยุดฟัง' : 'กดแล้วพูดได้เลย'
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={listening ? stop : start}
            disabled={!supported}
            sx={{
              color: listening ? '#dc2626' : 'primary.main',
              ...(listening && {
                animation: 'aiQMicPulse 1.2s ease-in-out infinite',
                '@keyframes aiQMicPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
              }),
            }}
          >
            {listening ? <StopCircleIcon /> : <MicIcon />}
          </IconButton>
        </span>
      </Tooltip>
    </InputAdornment>
  );
}

// หน้าต่างแก้ไขคำตอบ (pop-up แยก ช่องพิมพ์กว้าง) — แก้ได้เฉพาะคำตอบของตัวเอง; มีไมค์เหมือนช่องตอบ
function EditAnswerDialog({ questionId, answer, onClose, onSaved }) {
  const [draft, setDraft] = useState(answer.text);
  const [saving, setSaving] = useState(false);
  const { supported, listening, error, start, stop } = useSpeechDictation({ getText: () => draft, onText: setDraft });

  const close = () => { stop(); onClose(); };
  const save = async () => {
    const text = draft.trim();
    if (!text || saving) return;
    stop();
    setSaving(true);
    try {
      await api.put(`${API}/questions/${questionId}/answers/${answer._id}`, { text });
      await onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>แก้ไขคำตอบ</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={5}
          sx={{ mt: 0.5 }}
          placeholder={listening ? 'กำลังฟัง... พูดได้เลย' : 'พิมพ์หรือกดไมค์เพื่อพูดคำตอบ'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          InputProps={{
            endAdornment: (
              <Box sx={{ alignSelf: 'flex-start' }}>
                <MicAdornment supported={supported} listening={listening} start={start} stop={stop} />
              </Box>
            ),
          }}
        />
        {error && <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#b91c1c' }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={close}>ยกเลิก</Button>
        <Button variant="contained" disabled={!draft.trim() || saving} onClick={save}>บันทึกการแก้ไข</Button>
      </DialogActions>
    </Dialog>
  );
}

// หน้าต่างกรอกข้อความสั้นๆ ก่อนทำรายการ (ขอหลักฐาน/ปิดประเด็น/ส่งต่อผู้บริหาร/ไม่อนุมัติ/อนุมัติพร้อมแก้ข้อความ)
function NoteActionDialog({
  title, label, required = true, confirmLabel, color = 'primary', initial = '', hint = '', onClose, onConfirm,
}) {
  const [text, setText] = useState(initial);
  const [saving, setSaving] = useState(false);
  const confirm = async () => {
    if ((required && !text.trim()) || saving) return;
    setSaving(true);
    try {
      await onConfirm(text.trim());
      onClose();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        {hint && <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>{hint}</Typography>}
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label={label}
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{ mt: 0.5 }}
        />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>ยกเลิก</Button>
        <Button variant="contained" color={color} disabled={(required && !text.trim()) || saving} onClick={confirm}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// แนบหลักฐาน: ข้อความ และ/หรือไฟล์ (อัปโหลดผ่าน /upload/file ขนาดไม่เกิน 1MB)
function EvidenceDialog({ onClose, onSubmit }) {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if ((!text.trim() && !file) || saving) return;
    setSaving(true);
    setError('');
    try {
      let url = '';
      if (file) {
        const form = new FormData();
        form.append('file', file);
        const { data } = await api.post(
          `${process.env.REACT_APP_API_URL}/upload/file`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        url = data?.url || '';
        if (!url) throw new Error('upload');
      }
      await onSubmit({ text: text.trim(), url, name: file?.name || '' });
      onClose();
    } catch (e) {
      setError('แนบไม่สำเร็จ — ไฟล์ต้องไม่เกิน 1MB');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>แนบหลักฐาน</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="อธิบายหลักฐาน (เช่น เลขที่เอกสาร วันที่)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{ mt: 0.5 }}
        />
        <Box sx={{ mt: 1.5 }}>
          <Button component="label" size="small" variant="outlined">
            {file ? `ไฟล์: ${file.name}` : 'เลือกไฟล์ (รูป/PDF ไม่เกิน 1MB)'}
            <input hidden type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Button>
        </Box>
        {error && <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#b91c1c' }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>ยกเลิก</Button>
        <Button variant="contained" disabled={(!text.trim() && !file) || saving} onClick={submit}>แนบหลักฐาน</Button>
      </DialogActions>
    </Dialog>
  );
}

// คำถาม 1 ข้อ — แยกเป็น component เพื่อให้แต่ละข้อมีไมค์/ร่างคำตอบของตัวเอง (useSpeechDictation ใช้ได้ทีละช่อง)
function QuestionItem({ q, isAuthority, onChanged }) {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // คำตอบที่กำลังแก้ (เปิด pop-up)
  const [dialog, setDialog] = useState(null); // evidence | request | close | escalate | approve | dismiss | resolve
  const [showHistory, setShowHistory] = useState(false);
  const { supported: micSupported, listening, error: micError, start: startMic, stop: stopMic } =
    useSpeechDictation({ getText: () => draft, onText: setDraft });

  const status = STATUS_META[q.status] || { label: q.status, bg: '#e5e7eb', color: '#374151' };
  const priority = PRIORITY_META[q.priority] || PRIORITY_META.medium;
  const act = async (fn) => { await fn(); await onChanged(); };
  const post = (path, body) => act(() => api.post(`${API}/questions/${q._id}/${path}`, body || {}));

  const submit = async () => {
    const text = draft.trim();
    if (!text || saving) return;
    stopMic();
    setSaving(true);
    try {
      await post('answer', { text });
      setDraft('');
    } finally {
      setSaving(false);
    }
  };

  const unresolved = UNRESOLVED.includes(q.status);
  const btn = { size: 'small', sx: { fontSize: '0.72rem', py: 0.1, whiteSpace: 'nowrap' } };

  return (
    <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 1.5, opacity: ['closed', 'dismissed'].includes(q.status) ? 0.6 : 1 }}>
      <Stack direction="row" spacing={0.5} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ maxWidth: '68%' }}>
          <Chip size="small" label={priority.label} sx={{ bgcolor: priority.bg, color: priority.color, fontWeight: 700 }} />
          <Chip
            size="small"
            label={`ที่มา: ${q.source?.label || 'ไม่ระบุ'}`}
            sx={{ bgcolor: '#e0f2fe', color: '#075985', fontWeight: 600, maxWidth: '100%' }}
          />
        </Stack>
        {/* ใครเป็นคนถาม: AI ผู้ให้บริการ + โมเดลที่ใช้จริง (มุมขวาบนของแต่ละคำถาม) */}
        <Typography variant="caption" sx={{ textAlign: 'right', color: '#6d28d9', fontWeight: 700, lineHeight: 1.2 }}>
          {q.asked_by?.provider
            ? `ถามโดย AI: ${providerLabel(q.asked_by.provider)}${q.asked_by.model ? ` · ${q.asked_by.model}` : ''}`
            : 'ถามโดย AI (ไม่ระบุรุ่น)'}
        </Typography>
      </Stack>

      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'pre-wrap' }}>{q.text}</Typography>
      {q.source?.basis && (
        <Typography variant="caption" sx={{ display: 'block', color: '#475569', mt: 0.25 }}>เพราะ: {q.source.basis}</Typography>
      )}

      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        <Chip size="small" label={status.label} sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700 }} />
        {q.escalated && <Chip size="small" label="ส่งต่อผู้บริหารแล้ว" sx={{ bgcolor: '#dc2626', color: '#fff', fontWeight: 700 }} />}
        {q.erp_check?.state === 'confirmed' && (
          <Tooltip title={q.erp_check.detail || ''}>
            <Chip size="small" label="ERP ยืนยันการเปลี่ยนแปลง" sx={{ bgcolor: '#16a34a', color: '#fff' }} />
          </Tooltip>
        )}
        {q.erp_check?.state === 'unchanged' && <Chip size="small" label="ERP ยังไม่เปลี่ยน" variant="outlined" />}
        <Typography variant="caption" color="text.secondary">
          ถาม {fmtDate(q.asked_date)} · กำหนดตอบ{' '}
          <span style={{ color: q.overdue ? '#dc2626' : undefined, fontWeight: q.overdue ? 700 : 400 }}>
            {fmtDate(q.due_date)}{q.overdue ? ' (เกินกำหนด)' : ''}
          </span>
          {q.responsible?.name ? ` · ผู้รับผิดชอบ ${q.responsible.name}` : ''}
        </Typography>
      </Stack>
      {q.escalated && (
        <Typography variant="caption" sx={{ display: 'block', color: '#991b1b' }}>
          {q.escalated.auto ? 'ระบบส่งต่อ' : `${q.escalated.by} ส่งต่อ`}: {q.escalated.reason}
        </Typography>
      )}
      {q.ai_assessment?.verdict && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: q.ai_assessment.verdict === 'sufficient' ? '#166534' : '#9a3412', mt: 0.25 }}
        >
          AI ประเมินคำตอบ: {q.ai_assessment.verdict === 'sufficient' ? 'เพียงพอ' : 'ยังไม่เพียงพอ'}
          {q.ai_assessment.reason ? ` — ${q.ai_assessment.reason}` : ''} (การประเมินไม่ใช่การปิดเรื่อง)
        </Typography>
      )}

      {(q.answers || []).map((a) => (
        // คำตอบเป็นสีเลือดหมู แยกจากสีคำถาม (ตัวหนังสือดำ/น้ำเงิน)
        <Box key={a._id} sx={{ mt: 1, pl: 1.5, borderLeft: `3px solid ${MAROON}` }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: MAROON, fontWeight: 500 }}>{a.text}</Typography>
          <Typography variant="caption" color="text.secondary">
            {a.answered_by} · {dayjs(a.answered_at).format('D-M-YY HH:mm')}
            {a.edited_at ? ` (แก้ไขเมื่อ ${dayjs(a.edited_at).format('D-M-YY HH:mm')})` : ''}
          </Typography>
          {a.can_edit && !['closed', 'dismissed'].includes(q.status) && (
            <Button size="small" sx={{ ml: 1, minWidth: 0, py: 0, fontSize: '0.7rem' }} onClick={() => setEditing(a)}>แก้ไข</Button>
          )}
        </Box>
      ))}

      {(q.evidence || []).length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#115e59' }}>หลักฐาน</Typography>
          {q.evidence.map((e) => (
            <Stack key={e._id} direction="row" spacing={1} alignItems="center" sx={{ pl: 1 }}>
              <Typography variant="caption" sx={{ flex: 1, wordBreak: 'break-word' }}>
                {e.url ? <a href={e.url} target="_blank" rel="noreferrer">{e.name || 'ไฟล์แนบ'}</a> : null}
                {e.url && e.text ? ' — ' : ''}{e.text}
                <span style={{ color: '#6b7280' }}> ({e.added_by} {dayjs(e.added_at).format('D-M-YY')})</span>
              </Typography>
              {e.verified ? (
                <Chip size="small" label={`ยืนยันแล้ว โดย ${e.verified_by}`} sx={{ bgcolor: '#ccfbf1', color: '#115e59' }} />
              ) : (
                isAuthority && unresolved && (
                  <Button {...btn} onClick={() => act(() => api.put(`${API}/questions/${q._id}/evidence/${e._id}/verify`))}>
                    ยืนยันหลักฐาน
                  </Button>
                )
              )}
            </Stack>
          ))}
        </Box>
      )}

      {q.status === 'pending_review' && isAuthority && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button {...btn} variant="contained" onClick={() => setDialog('approve')}>อนุมัติคำถาม</Button>
          <Button {...btn} color="inherit" onClick={() => setDialog('dismiss')}>ไม่อนุมัติ</Button>
        </Stack>
      )}

      {ANSWERABLE.includes(q.status) && (
        <>
          {/* ช่องพิมพ์เต็มความกว้าง ปุ่มอยู่แถวล่างชิดขวา (ไม่แย่งที่ช่องพิมพ์) */}
          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={5}
            sx={{ mt: 1 }}
            placeholder={listening ? 'กำลังฟัง... พูดได้เลย' : 'พิมพ์หรือกดไมค์เพื่อพูดคำตอบ'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            InputProps={{
              endAdornment: (
                <MicAdornment supported={micSupported} listening={listening} start={startMic} stop={stopMic} />
              ),
            }}
          />
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.75 }}>
            <Button size="small" variant="contained" disabled={!draft.trim() || saving} onClick={submit}>ส่งคำตอบ</Button>
          </Stack>
          {micError && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#b91c1c' }}>{micError}</Typography>
          )}
        </>
      )}

      {/* แถวปุ่มตามสถานะ — เจ้าหน้าที่: แนบหลักฐาน/แก้ไขแล้ว/ส่งต่อ, ผู้มีอำนาจ: ขอหลักฐาน/ปิดประเด็น/เปิดซ้ำ */}
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        {unresolved && <Button {...btn} variant="outlined" onClick={() => setDialog('evidence')}>แนบหลักฐาน</Button>}
        {['answered', 'evidence_verified'].includes(q.status) && (
          <Button {...btn} variant="outlined" color="success" onClick={() => setDialog('resolve')}>ทำเครื่องหมายว่าแก้ไขแล้ว</Button>
        )}
        {unresolved && !q.escalated && (
          <Button {...btn} variant="outlined" color="error" onClick={() => setDialog('escalate')}>ส่งต่อผู้บริหาร</Button>
        )}
        {isAuthority && (unresolved || q.status === 'resolved') && (
          <Button {...btn} variant="outlined" color="warning" onClick={() => setDialog('request')}>ขอหลักฐานเพิ่ม</Button>
        )}
        {isAuthority && !['pending_review', 'closed', 'dismissed'].includes(q.status) && (
          <Button {...btn} variant="contained" color="inherit" onClick={() => setDialog('close')}>อนุมัติปิดประเด็น</Button>
        )}
        {isAuthority && ['closed', 'dismissed', 'resolved'].includes(q.status) && (
          <Button {...btn} variant="outlined" onClick={() => post('reopen')}>เปิดซ้ำ</Button>
        )}
        <Button {...btn} color="inherit" onClick={() => setShowHistory((v) => !v)}>{showHistory ? 'ซ่อนประวัติ' : 'ประวัติ'}</Button>
      </Stack>
      {showHistory && (
        <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid #e5e7eb' }}>
          {(q.history || []).map((h, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Typography key={i} variant="caption" sx={{ display: 'block', color: '#6b7280' }}>
              {dayjs(h.at).format('D-M-YY HH:mm')} · {h.by} · {h.action}
              {h.to && h.to !== h.from ? ` (${STATUS_META[h.to]?.label || h.to})` : ''}
              {h.note ? ` — ${h.note}` : ''}
            </Typography>
          ))}
        </Box>
      )}

      {editing && (
        <EditAnswerDialog questionId={q._id} answer={editing} onClose={() => setEditing(null)} onSaved={onChanged} />
      )}
      {dialog === 'evidence' && (
        <EvidenceDialog onClose={() => setDialog(null)} onSubmit={(body) => post('evidence', body)} />
      )}
      {dialog === 'request' && (
        <NoteActionDialog
          title="ขอหลักฐานเพิ่ม"
          label="ระบุหลักฐานที่ต้องการ"
          confirmLabel="ขอหลักฐาน"
          color="warning"
          onClose={() => setDialog(null)}
          onConfirm={(note) => post('request-evidence', { note })}
        />
      )}
      {dialog === 'close' && (
        <NoteActionDialog
          title="อนุมัติปิดประเด็น"
          label="หมายเหตุการปิด (ไม่บังคับ)"
          required={false}
          confirmLabel="อนุมัติปิด"
          hint="การปิดประเด็นคือการยืนยันโดยผู้มีอำนาจว่าเรื่องนี้เรียบร้อยแล้ว"
          onClose={() => setDialog(null)}
          onConfirm={(note) => act(() => api.put(`${API}/questions/${q._id}/close`, { note }))}
        />
      )}
      {dialog === 'escalate' && (
        <NoteActionDialog
          title="ส่งต่อผู้บริหาร"
          label="เหตุผลที่ต้องส่งต่อ"
          confirmLabel="ส่งต่อ"
          color="error"
          hint="เรื่องนี้จะขึ้นในหน้า Executive Alerts ครั้งเดียว"
          onClose={() => setDialog(null)}
          onConfirm={(reason) => post('escalate', { reason })}
        />
      )}
      {dialog === 'resolve' && (
        <NoteActionDialog
          title="ทำเครื่องหมายว่าแก้ไขแล้ว"
          label="สิ่งที่แก้ไข (ไม่บังคับ)"
          required={false}
          confirmLabel="ยืนยัน"
          hint="เรื่องจะรอผู้มีอำนาจอนุมัติปิดอีกขั้นหนึ่ง"
          onClose={() => setDialog(null)}
          onConfirm={(note) => post('resolve', { note })}
        />
      )}
      {dialog === 'approve' && (
        <NoteActionDialog
          title="อนุมัติคำถาม"
          label="ข้อความคำถาม (แก้ไขได้ก่อนอนุมัติ)"
          initial={q.text}
          confirmLabel="อนุมัติให้แสดง"
          onClose={() => setDialog(null)}
          onConfirm={(text) => post('review', { action: 'approve', text })}
        />
      )}
      {dialog === 'dismiss' && (
        <NoteActionDialog
          title="ไม่อนุมัติคำถาม"
          label="เหตุผล (ไม่บังคับ)"
          required={false}
          confirmLabel="ไม่อนุมัติ"
          color="inherit"
          onClose={() => setDialog(null)}
          onConfirm={(note) => post('review', { action: 'dismiss', note })}
        />
      )}
    </Box>
  );
}

// ปุ่ม "คำถามจาก AI" ในกล่อง Notes หน้า T-1 — ตัวเลขบนปุ่ม = คำถามที่รอตอบ/รอหลักฐาน (ไม่มีการแจ้งเตือนนอกหน้านี้)
// กดเปิด pop-up เห็นคำถาม + ที่มา + ความสำคัญ/กำหนดตอบ + กล่องตอบ (พิมพ์หรือพูดผ่านไมค์) เก็บคำตอบพร้อมชื่อผู้ตอบจากบัญชีที่ล็อกอิน
export function AiQuestionsButton({ projectId }) {
  const [openCount, setOpenCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [isAuthority, setIsAuthority] = useState(false);
  const [pendingReview, setPendingReview] = useState(0);
  const [filter, setFilter] = useState('active');

  const loadCount = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`${API}/questions/${projectId}/count`);
      setOpenCount(data?.open_count || 0);
    } catch (e) {
      setOpenCount(0);
    }
  }, [projectId]);

  const loadList = useCallback(async () => {
    const { data } = await api.get(`${API}/questions/${projectId}`);
    setRows(data?.rows || []);
    setOpenCount(data?.open_count || 0);
    setIsAuthority(!!data?.is_authority);
    setPendingReview(data?.pending_review_count || 0);
  }, [projectId]);

  useEffect(() => {
    loadCount();
  }, [loadCount]);

  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  const shown = rows.filter((q) => {
    if (filter === 'review') return q.status === 'pending_review';
    if (filter === 'active') return !['closed', 'dismissed'].includes(q.status);
    return true;
  });

  return (
    <>
      <Badge badgeContent={openCount} color="error" invisible={!openCount}>
        <Button
          size="small"
          variant="outlined"
          color={openCount ? 'warning' : 'inherit'}
          onClick={() => setOpen(true)}
          sx={{ fontSize: '0.7rem', py: 0.2, minWidth: 0 }}
        >
          คำถามจาก AI
        </Button>
      </Badge>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <span>คำถามจาก AI</span>
            <Select size="small" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ fontSize: '0.8rem', minWidth: 190 }}>
              <MenuItem value="active">ที่ยังไม่ปิด</MenuItem>
              <MenuItem value="all">ทั้งหมด (รวมที่ปิดแล้ว)</MenuItem>
              {isAuthority && <MenuItem value="review">{`รอหัวหน้าตรวจ (${pendingReview})`}</MenuItem>}
            </Select>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {shown.length === 0 && (
            <Typography variant="body2" color="text.secondary">ไม่มีคำถามในรายการนี้</Typography>
          )}
          <Stack spacing={2}>
            {shown.map((q) => (
              <QuestionItem key={q._id} q={q} isAuthority={isAuthority} onChanged={loadList} />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} autoFocus>ปิดหน้าต่าง</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AiQuestionsButton;
